import { useBusyAction } from '@/db/useBusyAction';
import logger from '@/util/logger';
import { AppStatusDao } from '@rock/db-admin';
import type { AppStatusDocKind, AppStatusDocMap } from '@rock/types';
import { toast } from 'sonner';

const log = logger('useAppStatusActions');
const appStatusDao = new AppStatusDao();

/**
 * Draft actions for one app-status incident tool: stage edits to the `_draft/db` mirror, promote
 * the draft to live (every app applies it within seconds), or discard it. Each tool publishes
 * independently — saving/publishing one never touches another's pending draft. Admins preview
 * staged drafts on-device via Settings → "Preview staged content".
 */
export function useAppStatusActions<K extends AppStatusDocKind>(kind: K) {
    const { busy, run } = useBusyAction(log, { kind });

    return {
        busy,
        saveDraft: (doc: AppStatusDocMap[K]) =>
            run('Save Draft', async () => {
                await appStatusDao.setAppStatusDraft(kind, doc);
                toast.success('Draft saved — preview it on-device before publishing');
                log.info('Saved app-status draft', { kind });
            }),
        publish: () =>
            run('Publish', async () => {
                await appStatusDao.publishAppStatus(kind);
                toast.success('Published — live in the app now');
                log.info('Published app-status', { kind });
            }),
        discardDraft: () =>
            run('Discard Draft', async () => {
                await appStatusDao.discardAppStatusDraft(kind);
                toast.success('Discarded draft changes');
                log.info('Discarded app-status draft', { kind });
            }),
    };
}
